import React, { memo, useState } from 'react';
import styled, { css } from 'styled-components';
import CheckBox from 'src/components/form/CheckBox';
import { Poppins } from 'src/common';
import Menu from '@mui/material/Menu';
import _ from 'lodash';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { useStateSelector } from 'src/redux';
import { TableIds } from 'src/api/types/misc';
import { useAuth } from 'src/state/auth';

const Div = styled.div<{ $isOpen: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  margin: 0 auto;
  z-index: 1;
  width: 100%;
  margin-right: 2px;

  svg {
    display: block;
  }

  &:before {
    content: '';
    width: 40px;
    height: 40px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:hover {
    cursor: pointer;
    #p-bg {
      fill: #fd563052;
    }
    #p-icon {
      fill: ${({ theme }) => theme.colors.gp1};
      stroke: ${({ theme }) => theme.colors.gp1};
    }
  }

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      #p-bg {
        fill: #fd563052;
      }
      #p-icon {
        fill: ${({ theme }) => theme.colors.gp1};
        stroke: ${({ theme }) => theme.colors.gp1};
      }
    `}
`;

const StyledMenu = styled(Menu)`
  .MuiList-padding {
    padding: 0 !important;
  }
  & .MuiPaper-root {
    border-radius: 3px;
    box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};
  }

  .gtcs-options {
    right: 0;
    padding-top: 18px;
    min-width: 250px;
    background-color: ${({ theme }) => theme.colors.white};
    border: 1px solid ${({ theme }) => theme.colors.stroke};
    cursor: default;
    display: grid;
    grid-gap: 10px;
    user-select: none;
    box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};
    z-index: 100;

    &__title {
      font-size: 14px;
      color: ${({ theme }) => theme.colors.cflowerBlue};
      margin: 0 20px;
      margin-bottom: 4px;
    }

    &__item {
      padding: 0 20px;
      display: flex;
      grid-gap: 10px;
      cursor: pointer;
      overflow: hidden;

      &__title {
        color: ${({ theme }) => theme.colors.cflowerBlue};
        font-weight: 500;
        font-size: 14px;
      }
    }

    &__clear {
      height: 40px;
      display: flex;
      justify-content: center;
      align-items: center;
      user-select: none;
      border-top: 1px solid ${({ theme }) => theme.colors.stroke};
      background-color: ${({ theme }) => theme.colors.cultured};
      margin-top: 4px;
      p {
        font-size: 14px;
        color: ${({ theme }) => theme.colors.cflowerBlue};
      }

      &:hover {
        cursor: pointer;
        background: ${({ theme }) => theme.colors.gradient};
        span {
          color: ${({ theme }) => theme.colors.btnWhite};
        }
      }
    }
  }
`;

interface GTColumnSelectAnchorProps {
  options: { key: string; title: string }[];
  value: string[];
  defaultOptions: string[];
  onChange: (value: string[]) => void;
  tableId: TableIds;
}

export const GTColumnSelectAnchor: React.FC<GTColumnSelectAnchorProps> = memo(
  ({ options, defaultOptions, onChange, value, tableId }) => {
    const colors = useStateSelector(({ theme }) => theme.colors);
    const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);
    const { features } = useAuth();

    const handleSelect = (key: string) => {
      let values: string[] = [];
      if (value.includes(key)) {
        values = value.filter((el) => el !== key);
      } else {
        values = [...value, key];
      }
      onChange(values);

      mpEvent(MPEvents.TableColumnsChange, { columns: values });
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      setAnchorEl(e.currentTarget);
      e.stopPropagation();
    };

    const handleResetToDefault = () => {
      onChange(defaultOptions);
      localStorage.removeItem(`${tableId}-column-sizing`);
      const event = new CustomEvent('column-sizing-reset', { detail: { tableId } });
      window.dispatchEvent(event);
      setAnchorEl(null);
    };

    const isAllSelected = options.every((el) => value.includes(el.key));

    const ignoredColumns: string[] = [];

    return (
      <>
        <Div onClick={handleClick} $isOpen={!!anchorEl} data-cy="gt-csa">
          <svg xmlns="http://www.w3.org/2000/svg" width="27" height="27" viewBox="0 0 27 27" fill="none">
            <rect
              x="0.75"
              y="0.75"
              width="25.5"
              height="25.5"
              rx="1.75"
              fill={colors.cultured}
              stroke={colors.stroke}
              strokeWidth="0.5"
              id="p-bg"
            />
            <path
              d="M8.53564 8.08L8.53564 18.92"
              stroke={colors.cflowerBlue}
              strokeWidth="1.84668"
              strokeLinecap="round"
              id="p-icon"
            />
            <path
              d="M13.46 8.08L13.46 18.92"
              stroke={colors.cflowerBlue}
              strokeWidth="1.84668"
              strokeLinecap="round"
              id="p-icon"
            />
            <path
              d="M18.3843 8.08L18.3843 18.92"
              stroke={colors.cflowerBlue}
              strokeWidth="1.84668"
              strokeLinecap="round"
              id="p-icon"
            />
          </svg>
        </Div>

        <StyledMenu
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          autoFocus={false}
          disableAutoFocusItem
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
              setAnchorEl(null);
            }
          }}
        >
          <div className="gtcs-options">
            <div
              className="gtcs-options__item"
              onClick={() => {
                onChange(isAllSelected ? [] : options.map((el) => el.key));
              }}
            >
              <CheckBox isChecked={isAllSelected} small />
              <Poppins className="gtcs-options__item__title" nowrap>
                All
              </Poppins>
            </div>

            {options
              .filter((el) => !ignoredColumns.includes(el.key))
              .map((el) => (
                <div
                  className="gtcs-options__item"
                  data-cy={`gt-csa-${el.key}`}
                  key={el.key}
                  onClick={() => {
                    handleSelect(el.key);
                  }}
                >
                  <CheckBox isChecked={value.includes(el.key)} small />
                  <Poppins className="gtcs-options__item__title" nowrap>
                    {el.title}
                  </Poppins>
                </div>
              ))}

            {!!defaultOptions && (
              <div className="gtcs-options__clear" onClick={handleResetToDefault}>
                <Poppins>Reset to default</Poppins>
              </div>
            )}
          </div>
        </StyledMenu>
      </>
    );
  },
  (prev, next) => _.isEqual(prev, next),
);
